/**
 * SEO metadata — single source of truth for per-page <title>,
 * <meta description>, canonical URLs and social share cards.
 *
 * Add a new public route here when you add it in App.js, and add the path to
 * scripts/routes.js so the sitemap and prerender pick it up.
 *
 * SITE.domain is read from REACT_APP_SITE_URL so staging and production do not
 * need a code change. scripts/generate-sitemap.js reads the same variable.
 */

export const SITE = {
  name: 'Ivipass',
  domain: (process.env.REACT_APP_SITE_URL || 'https://www.ivipass.com').replace(/\/+$/, ''),
  locale: 'en_GB',
  twitterHandle: process.env.REACT_APP_TWITTER_HANDLE || '',
  defaultDescription:
    'Ivipass helps individuals plan and complete their move abroad — university admissions, overseas employment, and residency routes to Canada, Australia, New Zealand and Portugal.',
  // Social share card. Generated asset pending (BRAND.md §7).
  defaultImage: '/og-image.png',
};

/**
 * Each entry:
 *   title       , full <title>, brand suffix included
 *   description , meta description, aim for ≤160 characters
 *   canonical?  , path override, for routes duplicating another route's content
 *   image?      , share-card override
 */
export const PAGES = {
  '/': {
    title: 'Ivipass | Study, Work and Residency Abroad',
    description:
      'Plan your move abroad with Ivipass. We guide individuals through university admissions, overseas employment and long-term residency, one stage at a time.',
  },
  '/study-abroad': {
    title: 'Study Abroad Support | University Applications | Ivipass',
    description:
      'Choosing courses, shortlisting universities, drafting personal statements and getting documents right — practical help through every step of an overseas application.',
  },
  '/work-abroad': {
    title: 'Work Abroad Support | Overseas Employment | Ivipass',
    description:
      'Support for finding and securing work overseas: matching roles to your background, preparing your documents, and getting you ready for employer interviews.',
  },
  '/pricing': {
    title: 'Pricing | What We Charge and What We Do Not | Ivipass',
    description:
      'A plain account of how Ivipass charges for study, work and residency support — what costs nothing, what carries a fee, and what you pay third parties directly.',
  },
  '/about': {
    title: 'About Ivipass | Who We Are',
    description:
      'Ivipass has supported people moving abroad since 2014. Read how we work, what we take on, and where our responsibilities end.',
  },
  '/contact': {
    title: 'Contact Ivipass | Speak to the Team',
    description:
      'Questions about studying, working or settling abroad? Reach the Ivipass team by email or phone, or arrange a consultation.',
  },

  // ─── Permanent Residency ───────────────────────────────────────────────
  '/permanent-residency/canada': {
    title: 'Canada Permanent Residency | Routes and Support | Ivipass',
    description:
      'Working through Canadian residency options — Express Entry, provincial nomination and family routes — from first eligibility check to submitted application.',
  },
  '/permanent-residency/australia': {
    title: 'Australia Permanent Residency | Routes and Support | Ivipass',
    description:
      'Australian residency routes explained: skilled migration, employer sponsorship and family streams, with help assessing where you realistically fit.',
  },
  '/permanent-residency/australia-care-workforce': {
    title: 'Australia Care Workforce Programme | Aged Care Roles | Ivipass',
    description:
      'A sponsored route into Australian aged care and disability support work, covering screening, training and the employer nomination process.',
  },
  '/permanent-residency/new-zealand': {
    title: 'New Zealand Permanent Residency | Routes and Support | Ivipass',
    description:
      'New Zealand residency routes including the Skilled Migrant Category and work-to-residence pathways, with document preparation and eligibility review.',
  },
  '/permanent-residency/portugal': {
    title: 'Portugal Permanent Residency | Routes and Support | Ivipass',
    description:
      'Portuguese residency routes covering D7, remote-work and family permits, with guidance on what each requires and how long it takes.',
  },

  // ─── Blog ──────────────────────────────────────────────────────────────
  '/blog': {
    title: 'Ivipass Journal | Notes on Moving Abroad',
    description:
      'Guides, checklists and honest assessments on studying and working abroad, written for people making the decision rather than searching for reassurance.',
  },
  '/blog/practical-guide-studying-abroad': {
    title: 'Studying Abroad: A Practical Walkthrough | Ivipass',
    description:
      'What actually happens between deciding to study overseas and holding an offer — narrowing your options, assembling documents, and applying well.',
  },
  '/blog/working-abroad-preparation': {
    title: 'Getting Ready to Work Overseas | Ivipass',
    description:
      'What overseas employers look for, how hiring differs across markets, and the paperwork worth preparing before you start applying.',
  },
  '/blog/informed-decisions-study-work-abroad': {
    title: 'Deciding Between Studying and Working Abroad | Ivipass',
    description:
      'Cost, timing, eligibility and long-term outcome — the trade-offs worth weighing before committing to a route abroad.',
  },

  // ─── Legal ─────────────────────────────────────────────────────────────
  '/terms-of-service': {
    title: 'Terms of Service | Ivipass',
    description:
      'The terms that apply when you use the Ivipass website or engage us for study, work or residency support.',
  },
  '/privacy-policy': {
    title: 'Privacy Policy | Ivipass',
    description:
      'What personal data Ivipass collects, why we hold it, how long we keep it, and the rights you have over it.',
  },
  '/cookie-policy': {
    title: 'Cookie Policy | Ivipass',
    description:
      'The cookies and similar technologies Ivipass uses across its website and client portal, and how to control them.',
  },
};

/**
 * Resolve metadata for a pathname. Falls back to homepage metadata for
 * unknown routes (e.g. the 404 page during prerender).
 */
export function getPageMeta(pathname) {
  return PAGES[pathname] || PAGES['/'];
}
